import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import { ReactElement, useState } from 'react';
import { dataTestIds } from 'src/constants/data-test-ids';
import { AdminHeaderActionSlot } from 'src/features/admin/AdminPageHeader';
import { EmCodeOption } from 'utils/lib/types/api/config/em-codes';
import { useEMCodes } from '../../../shared/hooks/useEMCodes';
import EMCodeDeleteDialog from './EMCodeDeleteDialog';
import EMCodeDialog from './EMCodeDialog';

interface DialogState {
  open: boolean;
  existingCode?: EmCodeOption;
}

export default function EMCodesAdminPage(): ReactElement {
  const { emCodes, isLoading, isError } = useEMCodes();
  const isMutating = useIsMutating({ mutationKey: ['em-codes'] }) > 0;

  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const [confirmDeleteCode, setConfirmDeleteCode] = useState<string | null>(null);

  const openAddDialog = (): void => setDialog({ open: true });

  const openEditDialog = (code: string, display: string): void =>
    setDialog({ open: true, existingCode: { code, display } });

  const closeDialog = (): void => setDialog({ open: false });

  return (
    <Box>
      <AdminHeaderActionSlot>
        <Button variant="contained" onClick={openAddDialog} data-testid={dataTestIds.emCodesAdminPage.addButton}>
          Add E&M Code
        </Button>
      </AdminHeaderActionSlot>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Failed to load E&M codes. Please try refreshing the page.</Typography>
      ) : emCodes.length === 0 ? (
        <Typography color="text.secondary">No E&M codes configured.</Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Display</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emCodes.map((entry) => (
                <TableRow key={entry.code} data-testid={dataTestIds.emCodesAdminPage.codeRow(entry.code)}>
                  <TableCell>{entry.code}</TableCell>
                  <TableCell>{entry.display}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(entry.code, entry.display)}
                        disabled={isMutating}
                        aria-label={`Edit E&M code ${entry.code}`}
                        data-testid={dataTestIds.emCodesAdminPage.editButton(entry.code)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDeleteCode(entry.code)}
                        disabled={isMutating}
                        color="error"
                        aria-label={`Delete E&M code ${entry.code}`}
                        data-testid={dataTestIds.emCodesAdminPage.deleteButton(entry.code)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EMCodeDialog open={dialog.open} onClose={closeDialog} existingCode={dialog.existingCode} />

      <EMCodeDeleteDialog code={confirmDeleteCode} onClose={() => setConfirmDeleteCode(null)} />
    </Box>
  );
}
